import { kvObj } from '../kvObj';

const API_URL = "https://cdn.contentful.com/spaces/64tq73zzgunt/environments/master/";
const ACCESS_TOKEN = "MQlbyLW_7WQ3TFI0AhT1zaIBbjA2mPQthdwVxGKCwEU";

// Cache storage
let cachedVendorsData: VendorData[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 15 * 60 * 1000; // 5 minutes in milliseconds

interface ContentfulAsset {
  sys: {
    id: string;
    type: string;
  };
  fields: {
    file: {
      url: string;
    };
  };
}

interface ContentfulEntry {
  sys: {
    id: string;
    type: string;
  };
  fields: {
    name: string;
    peopleNames?: string;
    sortName: string;
    description?: string;
    expo?: boolean;
    pic?: {
      sys: {
        id: string;
      };
    };
    spot?: string;
    categories?: string;
  };
}

interface VendorData {
  name: string;
  peopleNames?: string;
  sortName: string;
  description?: string;
  logo?: string;
  expo?: boolean;
  spot?: string;
  categories?: string;
}

// Helper function to check if cache is still valid
function isCacheValid(): boolean {
  if (!cachedVendorsData || !cacheTimestamp) {
    return false;
  }
  const now = Date.now();
  return (now - cacheTimestamp) < CACHE_DURATION;
}

// Function to clear cache manually if needed
export function clearContentfulCache(): void {
  cachedVendorsData = null;
  cacheTimestamp = null;
}

async function fetchAndCacheContentfulData(): Promise<VendorData[]> {
  try {
    const response = await fetch(`${API_URL}entries?access_token=${ACCESS_TOKEN}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Extract entries
    let entries: VendorData[] = data.items
      .filter((itm: ContentfulEntry) => itm.sys.type === 'Entry')
      .map((item: ContentfulEntry) => item.fields as VendorData);
    
    // Build images lookup
    const imagesById: { [key: string]: string } = {};
    if (data.includes && data.includes.Asset) {
      data.includes.Asset
        .filter((itm: ContentfulAsset) => itm.sys.type === 'Asset')
        .forEach((asset: ContentfulAsset) => {
          imagesById[asset.sys.id] = asset.fields.file.url;
        });
    }

    // Map images to entries
    entries = entries.map(entry => {
      const originalEntry = data.items.find((item: ContentfulEntry) => 
        item.fields.name === entry.name
      );
      
      if (originalEntry?.fields.pic?.sys.id) {
        const assetId = originalEntry.fields.pic.sys.id;
        const imageUrl = imagesById[assetId];
        if (imageUrl) {
          entry.logo = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
        }
      }
      
      return entry;
    });

    // Sort by sortName
    entries.sort((a, b) => a.sortName.localeCompare(b.sortName));
    
    // Cache the data
    cachedVendorsData = entries;
    cacheTimestamp = Date.now();
    
    return entries;
  } catch (error) {
    console.error("Error fetching Contentful data:", error);
    throw error;
  }
}

export async function fetchContentfulData(isExpo: boolean = false): Promise<VendorData[]> {
  // Check if cache is valid
  if (isCacheValid() && cachedVendorsData) {
    console.log("Using cached Contentful data");
    return cachedVendorsData.filter(itm => itm.expo === isExpo);
  }
  
  console.log("Fetching fresh Contentful data");
  const allVendors = await fetchAndCacheContentfulData();
  return allVendors.filter(itm => itm.expo === isExpo);
}

export async function getMapFromContentful(): Promise<kvObj> {
  try {
    // Fetch both expo and non-expo vendors (will use cache if available)
    const nonExpoVendors = await fetchContentfulData(false);
    const expoVendors = await fetchContentfulData(true);
    const allVendors = [...nonExpoVendors, ...expoVendors];
    
    // Build map object
    const map: kvObj = {};
    
    allVendors.forEach(vendor => {
      if (vendor.spot) {
        map[vendor.spot] = {
          name: vendor.name,
          categories: vendor.categories || vendor.name,
          real_name: vendor.peopleNames || vendor.name,
          description: vendor.description || ""
        };
      }
    });
    
    // Add updated timestamp
    map["updated"] = new Date().toLocaleDateString();
    
    return map;
  } catch (error) {
    console.error("Error building map from Contentful:", error);
    throw error;
  }
}

export async function getVendorDetailsFromContentful(vendorName: string): Promise<kvObj> {
  try {
    // Check cache first
    if (isCacheValid() && cachedVendorsData) {
      console.log("Using cached vendor data");
      const vendor = cachedVendorsData.find(v => v.name === vendorName);
      
      if (vendor) {
        return {
          description: vendor.description || "",
          real_name: vendor.peopleNames || vendorName,
          logo: vendor.logo || ""
        };
      }
    } else {
      // Fetch fresh data
      console.log("Fetching fresh vendor data");
      const nonExpoVendors = await fetchContentfulData(false);
      const expoVendors = await fetchContentfulData(true);
      const allVendors = [...nonExpoVendors, ...expoVendors];
      
      const vendor = allVendors.find(v => v.name.indexOf(vendorName.trim())>-1);
      
      if (vendor) {
        return {
          description: vendor.description || "",
          real_name: vendor.peopleNames || vendorName,
          logo: vendor.logo || ""
        };
      }
    }
    
    return {
      description: "",
      real_name: vendorName,
      logo: ""
    };
  } catch (error) {
    console.error("Error fetching vendor details:", error);
    return {
      description: "",
      real_name: vendorName,
      logo: ""
    };
  }
}
